import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import {
  FUNNEL_STEPS, STEP_IDS, getStep, getStepIndex, optionLabel, ICON_COLORS,
} from '@/lib/funnel';
import { useFunnel } from '@/context/FunnelContext';
import { getTracking, getSessionId } from '@/lib/tracking';
import { NotchedField } from '@/components/NotchedField';
import { Button } from '@/components/ui/button';

const Icon = ({ name, className }) => {
  const Cmp = Icons[name] || Icons.Circle;
  return <Cmp className={className} />;
};

const RED_BTN =
  'h-14 w-full rounded-xl bg-[#EF4444] hover:bg-[#DC2626] text-white font-bold text-lg shadow-sm transition-colors disabled:opacity-70';

/* ---------------- Single choice (colorful option cards) ---------------- */
function OptionCard({ stepId, option, onSelect }) {
  const c = ICON_COLORS[option.color] || ICON_COLORS.slate;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(option.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(option.id);
        }
      }}
      className="group cursor-pointer select-none rounded-2xl border border-slate-200 bg-white shadow-[0_8px_20px_rgba(15,23,42,0.06)] hover:shadow-[0_14px_30px_rgba(15,23,42,0.12)] hover:-translate-y-0.5 transition-shadow transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
      data-testid={`flow-option-${stepId}-${option.id}`}
    >
      <div className="p-[clamp(14px,2vh,24px)] flex flex-col items-center text-center gap-3">
        <div className={`h-[clamp(44px,6vh,56px)] w-[clamp(44px,6vh,56px)] rounded-xl border flex items-center justify-center ${c.wrap}`}>
          <Icon name={option.icon} className={`h-7 w-7 ${c.icon}`} />
        </div>
        <span className="font-semibold text-slate-900">{option.label}</span>
      </div>
    </div>
  );
}

/* ---------------- Address step (with verification) ---------------- */
function AddressStep({ answers, setAnswer, onNext }) {
  const [street, setStreet] = useState(answers.address || '');
  const [city, setCity] = useState(answers.city || '');
  const [state, setState] = useState(answers.state || '');
  const [zip, setZip] = useState(answers.zip || '');
  const [editLoc, setEditLoc] = useState(false);
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);

  // Resolve accurate city/state from the entered ZIP (more reliable than IP geo).
  useEffect(() => {
    if (!zip) return;
    let active = true;
    api
      .get(`/geo-zip?zip=${encodeURIComponent(zip)}`)
      .then((res) => {
        if (active && res.data.found) {
          setCity(res.data.city);
          setState(res.data.state);
          setAnswer('city', res.data.city);
          setAnswer('state', res.data.state);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!street.trim()) {
      setError('Please enter your street address.');
      return;
    }
    setError('');
    setVerifying(true);
    try {
      const res = await api.post('/verify-address', {
        address: street, city, state, zip,
      });
      if (res.data.valid) {
        setAnswer('address', street.trim());
        setAnswer('city', city);
        setAnswer('state', state);
        setAnswer('zip', zip);
        onNext();
      } else {
        setError("We couldn't verify that address. Please double-check and try again.");
      }
    } catch (err) {
      // fail open
      setAnswer('address', street.trim());
      onNext();
    } finally {
      setVerifying(false);
    }
  };

  return (
    <form onSubmit={submit} className="mx-auto max-w-xl w-full">
      <NotchedField
        label="Street Address"
        value={street}
        onChange={(e) => setStreet(e.target.value)}
        placeholder="123 Main St"
        error={error}
        data-testid="address-street-input"
        autoFocus
      />

      <div className="mt-3 text-sm text-slate-700 flex items-center gap-2 flex-wrap">
        {!editLoc ? (
          <>
            <span data-testid="address-location-line">
              {[city, state, zip].filter(Boolean).join(', ') || 'Location not detected'}
            </span>
            <button
              type="button"
              onClick={() => setEditLoc(true)}
              className="text-blue-600 font-medium hover:underline"
              data-testid="address-change-button"
            >
              Change
            </button>
          </>
        ) : (
          <div className="grid grid-cols-3 gap-2 w-full">
            <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City"
              className="h-10 rounded-lg border border-slate-300 px-2 text-sm outline-none focus:border-[#EF4444]" data-testid="address-city-input" />
            <input value={state} onChange={(e) => setState(e.target.value)} placeholder="State"
              className="h-10 rounded-lg border border-slate-300 px-2 text-sm outline-none focus:border-[#EF4444]" data-testid="address-state-input" />
            <input value={zip} onChange={(e) => setZip(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))} placeholder="Zip"
              className="h-10 rounded-lg border border-slate-300 px-2 text-sm outline-none focus:border-[#EF4444]" data-testid="address-zip-input" />
          </div>
        )}
      </div>

      <Button type="submit" disabled={verifying} className={`mt-7 ${RED_BTN}`} data-testid="address-continue-button">
        {verifying ? 'Verifying\u2026' : 'Continue'}
      </Button>
    </form>
  );
}

/* ---------------- Name step ---------------- */
function NameStep({ answers, setAnswer, onNext }) {
  const [first, setFirst] = useState(answers.first_name || '');
  const [last, setLast] = useState(answers.last_name || '');
  const [errors, setErrors] = useState({});

  const submit = (e) => {
    e.preventDefault();
    const er = {};
    if (!first.trim()) er.first = 'Please enter your first name.';
    if (!last.trim()) er.last = 'Please enter your last name.';
    setErrors(er);
    if (Object.keys(er).length) return;
    setAnswer('first_name', first.trim());
    setAnswer('last_name', last.trim());
    onNext();
  };

  return (
    <form onSubmit={submit} className="mx-auto max-w-xl w-full grid gap-4">
      <NotchedField label="First Name" value={first} onChange={(e) => setFirst(e.target.value)}
        placeholder="John" error={errors.first} data-testid="name-first-input" autoFocus />
      <NotchedField label="Last Name" value={last} onChange={(e) => setLast(e.target.value)}
        placeholder="Smith" error={errors.last} data-testid="name-last-input" />
      <Button type="submit" className={`mt-3 ${RED_BTN}`} data-testid="name-continue-button">Continue</Button>
    </form>
  );
}

/* ---------------- Email step ---------------- */
function EmailStep({ answers, setAnswer, onNext }) {
  const [email, setEmail] = useState(answers.email || '');
  const [error, setError] = useState('');

  const submit = (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    setAnswer('email', email.trim());
    onNext();
  };

  return (
    <form onSubmit={submit} className="mx-auto max-w-xl w-full">
      <NotchedField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder="you@email.com" error={error} data-testid="email-input" autoFocus />
      <Button type="submit" className={`mt-7 ${RED_BTN}`} data-testid="email-continue-button">Continue</Button>
    </form>
  );
}

/* ---------------- Phone step (final) ---------------- */
function PhoneStep({ answers, setAnswer, onSubmit, submitting }) {
  const [phone, setPhone] = useState(answers.phone || '');
  const [error, setError] = useState('');

  const submit = (e) => {
    e.preventDefault();
    if (!/^[0-9+()\-\s]{7,}$/.test(phone.trim())) {
      setError('Please enter a valid phone number.');
      return;
    }
    setAnswer('phone', phone.trim());
    onSubmit(phone.trim());
  };

  return (
    <form onSubmit={submit} className="mx-auto max-w-xl w-full">
      <NotchedField label="Phone Number" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
        placeholder="(555) 123-4567" error={error} data-testid="phone-input" autoFocus />
      <Button type="submit" disabled={submitting} className={`mt-7 ${RED_BTN}`} data-testid="phone-submit-button">
        {submitting ? 'Submitting\u2026' : 'View Results'}
      </Button>
      <p className="mt-6 text-[11px] leading-relaxed text-slate-400 text-center">
        By clicking the button above, I am providing my electronic signature authorizing Open Sesame Garage
        Doors and up to four home service companies to contact me by phone, text, and email at the number and
        address provided, including via automated technology and pre-recorded messages, even if my number is on
        a Do-Not-Call list. Consent is not a condition of purchase. Message and data rates may apply. You may
        revoke consent at any time. You also agree to our{' '}
        <span className="text-blue-500">terms</span> and <span className="text-blue-500">privacy policy</span>.
      </p>
    </form>
  );
}

/* ---------------- Main ---------------- */
export default function FunnelStep() {
  const { step: stepId } = useParams();
  const navigate = useNavigate();
  const { answers, setAnswer } = useFunnel();
  const [submitting, setSubmitting] = useState(false);

  const step = getStep(stepId);
  const index = getStepIndex(stepId);
  const total = STEP_IDS.length;

  useEffect(() => {
    if (!answers.zip) {
      navigate('/', { replace: true });
      return;
    }
    if (!step) navigate('/flow/homeowner', { replace: true });
    window.scrollTo(0, 0);
  }, [stepId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!step) return null;

  const goNext = () => {
    if (index < total - 1) navigate(`/flow/${STEP_IDS[index + 1]}`);
  };

  const selectOption = (optionId) => {
    setAnswer(step.field, optionLabel(stepId, optionId));
    setTimeout(goNext, 140);
  };

  const submitLead = async (phoneVal) => {
    setSubmitting(true);
    try {
      const tracking = getTracking();
      const payload = {
        zip: answers.zip || '',
        homeowner: answers.homeowner || '',
        issue: answers.issue || '',
        service_type: answers.service_type || '',
        address: answers.address || '',
        city: answers.city || '',
        state: answers.state || '',
        first_name: answers.first_name || '',
        last_name: answers.last_name || '',
        email: answers.email || '',
        phone: phoneVal || answers.phone || '',
        session_id: getSessionId(),
        campaign_id: tracking.campaign_id,
        adgroup_id: tracking.adgroup_id,
        ad_id: tracking.ad_id,
        keyword: tracking.keyword,
        gclid: tracking.gclid,
        params: tracking.params,
      };
      await api.post('/leads', payload);
      navigate('/thank-you');
    } catch (e) {
      toast.error('Something went wrong submitting your request. Please try again.');
      setSubmitting(false);
    }
  };

  const gridCols = step.columns === 3 ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2';

  return (
    <div
      className="min-h-full flex flex-col justify-center px-4 sm:px-6 pt-[clamp(12px,2.5vh,32px)] pb-[clamp(88px,15vh,170px)]"
      data-testid="page-flow"
    >
      <motion.div
        key={stepId}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18 }}
        className="w-full max-w-4xl mx-auto"
        data-testid="flow-step-container"
      >
        <div className="text-center mb-[clamp(16px,3vh,32px)]">
          <h2 className="font-slab font-extrabold text-[clamp(1.5rem,3.6vw,2.25rem)] text-slate-900 leading-tight" data-testid="flow-question">
            {step.question}
          </h2>
          {step.subtitle && (
            <p className="mt-3 text-[clamp(0.95rem,1.7vw,1.125rem)] font-semibold text-[#EF4444]" data-testid="flow-subtitle">{step.subtitle}</p>
          )}
        </div>

        {step.type === 'single' && (
          <div className={`grid gap-[clamp(10px,1.6vh,16px)] ${gridCols} max-w-2xl mx-auto`}>
            {step.options.map((opt) => (
              <OptionCard key={opt.id} stepId={stepId} option={opt} onSelect={selectOption} />
            ))}
          </div>
        )}
        {step.type === 'address' && <AddressStep answers={answers} setAnswer={setAnswer} onNext={goNext} />}
        {step.type === 'name' && <NameStep answers={answers} setAnswer={setAnswer} onNext={goNext} />}
        {step.type === 'email' && <EmailStep answers={answers} setAnswer={setAnswer} onNext={goNext} />}
        {step.type === 'phone' && (
          <PhoneStep answers={answers} setAnswer={setAnswer} onSubmit={submitLead} submitting={submitting} />
        )}
      </motion.div>
    </div>
  );
}
